import { useEffect, useRef, type ReactNode, type SyntheticEvent } from 'react'
import { Lock } from 'lucide-react'
import { emit } from '../analytics/analytics'
import type { ENUM_VALUES } from '../analytics/vocabulary'
import { Icon } from '../icons/Icon'
import { Skeleton } from '../async/Skeleton'
import { Button } from '../primitives/Button'
import { Panel } from '../primitives/Panel'
import { useEntitlement, useEntitlementsLoading } from './useEntitlement'
import type { Entitlement } from './entitlements'
import './premiumGate.css'

/** The panels a gate can name on `premium_gate_shown`: the closed set `vocabulary.ts` accepts. */
export type PanelKind = (typeof ENUM_VALUES)['panel_kind'][number]

/**
 * A gate renders a **locked preview**, never an absence.
 *
 * A missing feature teaches nothing — the user cannot want what they cannot
 * see. A locked one shows what the upgrade buys.
 *
 * The real children are not rendered when locked, only the preview. Blurring
 * the real content with CSS would leave it mounted, fetching, and readable in
 * the DOM, which for premium content means giving it away to anyone who opens
 * devtools.
 */

type Props = {
  entitlement: Entitlement
  /**
   * What the feature looks like — blurred behind the upgrade prompt.
   *
   * **MUST be presentational: no control, no handler.** The gate's `inert` and
   * its event swallow are defence in depth, not licence to pass a live control
   * (#527 began as exactly that assumption).
   */
  preview: ReactNode
  children: ReactNode
  /** Human-readable feature name for the button's accessible name. Defaults to
   *  the table below. */
  label?: string
  /**
   * Defaults to `outline`, not `primary`. "One lime surface per screen —
   * `--accent` is the primary action or the hero tile, not both" — and a gate
   * cannot see its screen, so three gates plus a HeroTile would have made four.
   * A single hero gate on an otherwise plain page can opt into `primary`.
   */
  variant?: 'primary' | 'outline'
  onUpgrade?: () => void
  /**
   * `'overlay'` (default): the centred Upgrade button over the preview, as every
   * caller before M3 drew it. `'none'`: the preview, `inert`, the swallow, the
   * blur and the emit all stay, and NO button is drawn — a small "Locked" badge
   * takes its place, read as "Locked — <feature>", so the gate still says it is
   * locked to a sighted user and to a screen reader alike (the preview itself
   * is `inert`, so without the badge assistive tech heard nothing at all). For a screen that holds
   * several gates under ONE upgrade prompt of its own — Cashflow's chart and
   * categories — where a button per gate put three identical "Upgrade"s on one
   * page (the defect `Accounts.tsx` caught as "two indistinguishable Upgrade
   * buttons"). A caller passing `'none'` owns the upgrade action elsewhere.
   */
  cta?: 'overlay' | 'none'
  /**
   * Which panel this gate locks, carried on `premium_gate_shown` as
   * `panel_kind`. Absent, the payload is the bare `{ entitlement }` it always
   * was. Typed from the closed set in `vocabulary.ts`, so the compiler refuses
   * a value `emit` would drop — and `emit` throws only under DEV.
   */
  panelKind?: PanelKind
}

/**
 * Written out rather than derived. `'ai_features'.replace(/_/g, ' ')` yields
 * "Upgrade to see ai features". There are exactly seven names and the list is
 * frozen, so a table cannot drift and cannot produce that.
 */
const FEATURE_NAME: Record<Entitlement, string> = {
  unlimited_accounts: 'unlimited accounts',
  cashflow_details: 'cash-flow detail',
  ai_features: 'AI features',
  spending_insights: 'spending insights',
  custom_categories: 'custom categories',
  custom_merchants: 'custom merchants',
  export_data: 'data export',
}

/**
 * The accessible name of an Upgrade action for `entitlement`. Exported so a
 * screen drawing its own Upgrade (Cashflow's single prompt above `cta="none"`
 * gates) carries the same name as the gate's, from the same table.
 */
export function upgradeLabel(entitlement: Entitlement, label?: string): string {
  return `Upgrade to see ${label ?? FEATURE_NAME[entitlement]}`
}

/**
 * A BACKSTOP, not the guarantee. The contract is on the `preview` prop: it must
 * be presentational. `inert` is a browser hint that Playwright and a directly
 * dispatched event ignore (#527 — Accounts passed its navigating Connect
 * button), so this capture-phase swallow stops a stray control from acting.
 *
 * Covered: click, double-click, aux-click, mouse down/up, pointer down/up, key
 * down/up and submit. `preventDefault` stops a native `<a href>` and a form
 * submit; `stopPropagation` in the capture phase skips the control's own React
 * handler.
 *
 * NOT covered: `onChange`/`onInput` (a value set by assistive tech, paste or
 * drop), touch events, `contextmenu`, `focus`, and any event type added later.
 */
function swallow(e: SyntheticEvent) {
  e.preventDefault()
  e.stopPropagation()
}

export function PremiumGate({
  entitlement,
  preview,
  children,
  label,
  variant = 'outline',
  onUpgrade,
  cta = 'overlay',
  panelKind,
}: Props) {
  const entitled = useEntitlement(entitlement)
  const loading = useEntitlementsLoading()

  /**
   * `premium_gate_shown` — registered since M0 and never emitted until #399.
   * Wired HERE rather than at each call site: `Categories.tsx`'s own
   * `locked_create_shown` effect is the precedent, but that event is one
   * SCREEN's more specific "create was locked" moment, and repeating its
   * ref-guarded effect at every `<PremiumGate>` caller (`Accounts.tsx`'s
   * toolbar, `Connect.tsx`'s choose screen, and every one before this ticket)
   * would drift the moment one of them forgot the guard. This component sees
   * every one of them at once.
   *
   * Fires once per transition INTO locked, not once per render — the same
   * ref-guard shape `MaintenanceGate.tsx` uses for `maintenance_shown` and
   * `Categories.tsx` for `locked_create_shown`, both needed because a parent
   * re-render (a breakpoint change, an unrelated state update) would
   * otherwise re-announce a lock state that never changed, and because
   * `<StrictMode>` double-invokes an effect on mount with the dependency
   * unchanged between the two calls — a hazard `[entitled]` alone does not
   * prevent.
   *
   * **`useEntitlement` is closed-by-default**, so "not known yet" reads the
   * same as "locked". Billing (#102) loads asynchronously, so the context also
   * carries `loading`: while it is true this gate renders a placeholder and
   * emits nothing, and the event fires only for a lock that has RESOLVED.
   * A failed read settles as locked, so it does announce.
   */
  const announcedLocked = useRef(false)
  useEffect(() => {
    if (loading) return
    if (!entitled && !announcedLocked.current) {
      announcedLocked.current = true
      emit('premium_gate_shown', panelKind === undefined ? { entitlement } : { entitlement, panel_kind: panelKind })
    } else if (entitled) {
      announcedLocked.current = false
    }
  }, [entitled, loading, entitlement, panelKind])

  if (entitled) return <>{children}</>
  if (loading) {
    return (
      <div aria-busy="true" data-entitlement={entitlement} data-loading="true" style={{ minHeight: 64 }}>
        <Skeleton height={64} />
      </div>
    )
  }

  const name = label ?? FEATURE_NAME[entitlement]

  return (
    // minHeight so the absolutely-positioned overlay cannot overflow onto
    // whatever sits below when a caller passes a short preview.
    <div data-entitlement={entitlement} style={{ position: 'relative', minHeight: 64 }}>
      {/* `inert`, not `aria-hidden` + `pointerEvents`.

          A preview can carry its own controls, and a locked feature's must be
          reachable by neither pointer nor keyboard. `aria-hidden` hides from
          the accessibility tree but leaves the subtree focusable; `pointer-
          events: none` blocks the mouse and nothing else. Together they left a
          keyboard user able to Tab onto a control that AT could not describe —
          WCAG 4.1.2. `inert` covers all three concerns at once, so both of the
          others are gone rather than kept alongside it. */}
      <div
        inert
        style={{ filter: 'blur(3px)', opacity: 0.55 }}
        onClickCapture={swallow}
        onDoubleClickCapture={swallow}
        onAuxClickCapture={swallow}
        onMouseDownCapture={swallow}
        onMouseUpCapture={swallow}
        onPointerDownCapture={swallow}
        onPointerUpCapture={swallow}
        onKeyDownCapture={swallow}
        onKeyUpCapture={swallow}
        onSubmitCapture={swallow}
      >
        {preview}
      </div>
      {cta === 'none' && (
        <p className="premium-gate__badge">
          <Icon glyph={Lock} size={12} />
          <span aria-hidden="true">Locked</span>
          <span className="sr-only">Locked — {name}</span>
        </p>
      )}
      {cta === 'overlay' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
          }}
        >
          {/* The CTA sits in a Panel, not bare on the preview.

              `.btn--outline` is `background: transparent`, so a bare button's
              `--ink` label takes whatever contrast the caller's preview happens
              to give it. web-tester measured 3.38:1 worst / 4.13:1 mean in dark
              over a lime-dominant preview — failing CLAUDE.md's 4.5:1 on the
              MEAN, not merely on stray pixels — and 3.38:1 over --accent-soft and
              bare --canvas too, because in dark the preview's own near-white text
              blurs under a near-white label. `blur(3px)` + `opacity: 0.55` lifts
              the documented 1.12:1 case but not past the bar.

              A Panel gives it --surface and the --line hairline, so contrast
              becomes CONSTANT — 12.54 light / 17.32 dark for every backdrop
              measured — instead of a function of the preview. That is the
              property #104 asked for. Reverting to `variant="primary"` also
              clears 4.5 but reintroduces the lime surface `outline` was chosen to
              avoid, trading an accessibility defect for a design-system one.

              The Panel carries no extra marker here. It used to need one:
              `.on-accent`'s DESCENDANT rules kept firing for a gate placed inside
              a HeroTile, because the tile was still an ancestor even though this
              Panel had changed the backdrop to --surface — near-black on
              near-black at 1.23:1 in dark. Under #98 the Panel hands down its own
              `--control-ink`, so the reset is a property of being a Panel rather
              than something this call site has to remember. */}
          <Panel style={{ padding: '12px 14px', gap: 0, alignItems: 'center' }}>
            {/* The feature name is the ACCESSIBLE name; the visible label is one
                word. That is what this component's `label` prop has always said
                it was for — "for the button's accessible name" — and rendering it
                as visible text instead was a drift that only showed on a
                production build.

                **Why it could not stay visible.** This overlay is `inset: 0` on a
                box sized by the PREVIEW, so its width is the width of the control
                it covers. M11's preview is a "New category" button, ~127px, which
                leaves the CTA about 99px. "Upgrade to see custom categories" wraps
                to four lines in that space, and a wrapped `.btn` is an ELLIPSE —
                `--r-pill` is 99px, and a 99px radius on a 66px-tall box resolves
                to an ellipse rather than a stadium, with the corners of the text
                outside the fill. Measured at every width from 320 to 1280, not
                only on phones.

                Widening the panel instead was rejected: it is absolutely
                positioned, so it would overlap whatever sits beside the preview
                rather than push it aside — here, the page title.

                WCAG 2.5.3 holds: the visible string "Upgrade" is the start of the
                accessible name, so voice control targeting what it reads still
                works. */}
            <Button
              variant={variant}
              size="panel"
              onClick={onUpgrade}
              aria-label={upgradeLabel(entitlement, label)}
            >
              Upgrade
            </Button>
          </Panel>
        </div>
      )}
    </div>
  )
}
